import { useMemo, useRef, useState } from "react"
import { cloneDeep } from "lodash"

import SectionContainer from "@/app/report/components/SectionContainer"
import MultipleChart from "@/app/report/components/chart/MultipleChart"
import VerticalPlayerChart from "@/app/report/components/chart/VerticalPlayerChart"
import { resolveGraphOrientation } from "@/app/report/common/sections/graph"
import SectionMenu from "@/app/report/components/SectionMenu"
import { ExportChartRefType, MenuActionType } from "@/app/report/common/type"
import { ChartData, SectionGraph } from "@/app/report/common/constructor"
import {
  DEFAULT_EXPORT_OPTIONS,
  DIVE_METRICS,
  REPORT_TYPE,
  VALUE_POSITION_TYPE,
} from "@/app/report/common/constants"
import ExportChartDialog from "@/app/report/components/dialog/ExportChartDialog"
import { usePositionRank } from "@/hooks/usePositionRank"
import { useTranslateMetricKey } from "@/hooks/metric/useTranslateKeyHook"
import { buildReportSortKey, createReportSortStore } from "@/stores/reportSort"
import { sortChartRows } from "@/app/report/common/export/sortRows"

function ChartByPlayer({
  originalChartData,
  loading,
  storeKey,
}: {
  originalChartData: ChartData
  loading: boolean
  storeKey: string
}) {
  // 화면 정렬은 리포트 스코프 store 에 보존(템플릿 미저장). 없으면 템플릿(originalChartData.sort) seed.
  const storedSort = createReportSortStore((state) => state.sorts[storeKey])
  const setSort = createReportSortStore((state) => state.setSort)

  const sortValue: string[] = storedSort
    ? [storedSort.key, storedSort.order]
    : originalChartData.sort
      ? [originalChartData.sort.key, originalChartData.sort.order.toLowerCase()]
      : []

  const [exportFileType, setExportFileType] = useState<string>("")
  const [currentDecimalPoints, setCurrentDecimalPoints] = useState<number>(1)
  const [currentAnnotationOptions, setCurrentAnnotationOptions] = useState<VALUE_POSITION_TYPE>(
    VALUE_POSITION_TYPE.HIDE_VALUES,
  )

  const translateKey = useTranslateMetricKey()
  const positionRank = usePositionRank()

  const chartRef = useRef<ExportChartRefType>(null)

  const menuItems = useMemo(() => {
    /* chart by player: value, player name, position
    export pdf, png image는 공통 필수 */
    const menuOptions = [] as MenuActionType[]
    menuOptions.push({
      name: "value",
      value: "value",
      group: "sort",
      children: originalChartData.settings.yAxis.map((yAxis) => {
        const newMetricName = translateKey(yAxis.metric)

        return {
          name: newMetricName,
          value: yAxis.metric,
          color: yAxis.color,
          children: [
            {
              name: "maxMin",
              value: `${yAxis.metric}MaxMin`,
              func: () => {
                setSort(storeKey, { key: yAxis.metric, order: "desc" })
              },
            },
            {
              name: "minMax",
              value: `${yAxis.metric}MinMax`,
              func: () => {
                setSort(storeKey, { key: yAxis.metric, order: "asc" })
              },
            },
          ],
        }
      }),
    })
    menuOptions.push({
      name: "playerName",
      value: "playerName",
      group: "sort",
      children: [
        {
          name: "A - Z",
          value: "playerNameAZ",
          func: () => setSort(storeKey, { key: "playerName", order: "asc" }),
        },
        {
          name: "Z - A",
          value: "playerNameZA",
          func: () => setSort(storeKey, { key: "playerName", order: "desc" }),
        },
      ],
    })
    menuOptions.push({
      name: "position",
      value: "position",
      group: "sort",
      children: [
        {
          name: "FW - GK",
          value: "positionFWGK",
          func: () => setSort(storeKey, { key: "position", order: "asc" }),
        },
        {
          name: "GK - FW",
          value: "positionGKFW",
          func: () => setSort(storeKey, { key: "position", order: "desc" }),
        },
      ],
    })
    menuOptions.push(
      ...DEFAULT_EXPORT_OPTIONS.map((option) => ({
        ...option,
        func: () => {
          setExportFileType(option.value)
        },
      })),
    )

    return menuOptions
  }, [originalChartData])

  const applyDiveMetricFilter = (data: ChartData): ChartData => {
    const newData = cloneDeep(data)
    const diveMetricIndices = newData.axisY
      .map((axis, index) => (DIVE_METRICS.includes(axis.key) ? index : -1))
      .filter((index) => index !== -1)

    if (diveMetricIndices.length === 0) {
      return newData
    }

    newData.data = newData.data.map((row) => {
      const position = row.header.get("position") as string
      if (position !== "GK") {
        diveMetricIndices.forEach((index) => {
          row.data[index] = null
        })
      }

      return row
    })

    return newData
  }

  const chartData = useMemo(() => {
    if (!sortValue.length) {
      return applyDiveMetricFilter(originalChartData)
    }

    const newChartData = cloneDeep(originalChartData)
    newChartData.data = sortChartRows(
      newChartData.data,
      newChartData.axisY,
      { key: sortValue[0], order: sortValue[1] as "asc" | "desc" },
      positionRank,
    )

    return applyDiveMetricFilter(newChartData)
  }, [sortValue, originalChartData, positionRank])

  const setOpen = (open: boolean) => {
    if (!open) {
      setExportFileType("")
    }
  }

  return (
    <>
      <SectionContainer
        title={chartData.settings.title}
        sectionMenu={chartData.data.length ? <SectionMenu menuItems={menuItems} /> : undefined}>
        {/* 세로(선수 세로 나열)는 별도 렌더러 — 템플릿 Graph 설정의 orientation 을 따른다 (#185) */}
        {resolveGraphOrientation(chartData.settings) === "vertical" ? (
          <VerticalPlayerChart chartData={chartData} loading={loading} ref={chartRef} />
        ) : (
          <MultipleChart chartData={chartData} loading={loading} ref={chartRef} />
        )}
      </SectionContainer>

      <ExportChartDialog
        open={!!exportFileType}
        setOpen={setOpen}
        decimalPoints={currentDecimalPoints}
        annotationOptions={currentAnnotationOptions}
        onConfirm={() => {
          // TODO export action
          chartRef.current?.exportFile({
            exportType: exportFileType as "png" | "pdf",
            magnification: 1.0,
            reportType: window.location.pathname.includes(REPORT_TYPE.DAILY)
              ? REPORT_TYPE.DAILY
              : REPORT_TYPE.TREND,
            decimalPoints: currentDecimalPoints,
            chartAnnotation: currentAnnotationOptions,
          })
        }}
        onChangeDecimalPoints={(value: number) => setCurrentDecimalPoints(value)}
        onChangeAnnotationOptions={(value: VALUE_POSITION_TYPE) =>
          setCurrentAnnotationOptions(value)
        }
      />
    </>
  )
}

export default function ChartByPlayers({
  reportSection,
  loading,
  sectionIndex = 0,
  reportType = REPORT_TYPE.DAILY,
}: {
  reportSection: SectionGraph
  loading: boolean
  sectionIndex?: number
  reportType?: REPORT_TYPE
}) {
  return (
    <>
      {reportSection?.charts.map((chart: ChartData, index: number) => {
        return (
          <ChartByPlayer
            originalChartData={chart}
            loading={loading}
            storeKey={buildReportSortKey(reportType, sectionIndex, index)}
            key={`ChartByPlayer-${index}`}
          />
        )
      })}
    </>
  )
}
